/**
 * BA: Live-updates the "$X" label and the drawn fill for the single-handle
 * price range slider (see snippets/ba-price-range-slider.liquid) while dragging.
 *
 * This intentionally does NOT wrap the slider in a Component-derived
 * custom element: Horizon's Component base class assigns each `[ref]`
 * descendant to the nearest ancestor whose tag name ends in `-component`
 * (see assets/component.js `#isDescendant`/`getClosestComponent`), so a
 * second such element nested inside <price-facet-component> would steal
 * its `minInput`/`maxInput` refs and silently break Horizon's own
 * filtering logic. Plain event delegation avoids that entirely.
 *
 * Filtering submission itself is untouched — it's still Horizon's own
 * `on:change="/updatePriceFilterAndResults"` on <price-facet-component>
 * (assets/facets.js), which only fires on drag-release/commit, not on
 * every `input` tick, so no debouncing is needed here either.
 *
 * No load-time initialisation is needed: the snippet renders both the label and
 * the initial `--ba-range-ratio` inline, and a Section Rendering API re-render
 * replaces the markup with freshly-rendered Liquid, so the two stay in step.
 */
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

/**
 * Positions the drawn fill. The value is already step-snapped by the browser,
 * so this matches the thumb exactly.
 * @param {HTMLInputElement} input
 */
function syncFill(input) {
  const min = Number(input.min) || 0;
  const max = Number(input.max);
  if (!Number.isFinite(max) || max <= min) return;

  const ratio = (Number(input.value) - min) / (max - min);
  input.style.setProperty('--ba-range-ratio', `${Math.min(1, Math.max(0, ratio))}`);
}

/**
 * Formats the slider's value with Horizon's own money formatter, so the label
 * is byte-identical to what the `money` Liquid filter renders server-side —
 * `Number#toLocaleString` would drop the currency's decimals and disagree with
 * the scale beneath the bar.
 * @param {HTMLInputElement} input
 * @param {HTMLElement} wrapper
 * @returns {string | null}
 */
function formatValue(input, wrapper) {
  const moneyFormat = wrapper.dataset.moneyFormat || '${{amount}}';
  const currency = wrapper.dataset.currency || 'USD';
  // The slider's value is in major units (dollars); formatMoney takes minor.
  const minorUnits = convertMoneyToMinorUnits(input.value, currency);

  if (minorUnits == null) return null;

  return formatMoney(minorUnits, moneyFormat, currency);
}

document.addEventListener('input', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || target.type !== 'range') return;

  const wrapper = target.closest('price-facet-component.ba-price-range__component');
  if (!(wrapper instanceof HTMLElement)) return;

  syncFill(target);

  const label = wrapper.querySelector('[ref="ba_priceOut"]');
  if (!label) return;

  const formatted = formatValue(target, wrapper);
  if (formatted != null) label.textContent = formatted;
});
